/** Права доступа к разделам CRM (синхрон с backend App\Support\Permissions). */

import { flattenAdminRoutes } from 'utils/adminRoutes';
import {
  isRouteHiddenInMirrorLedger,
  isRouteHiddenInTaxView,
  userUsesMirrorLedger,
  userUsesTaxView,
} from 'utils/taxViewMode';

export const PERMISSIONS = {
  DASHBOARD: 'dashboard',
  DASHBOARD_FINANCE: 'dashboard.finance',
  SCHEDULE: 'schedule',
  UPCOMING_APPOINTMENTS: 'upcoming_appointments',
  CLIENTS: 'clients',
  RECEPTION_LEADS: 'reception_leads',
  RECEPTION_SHIFTS: 'reception_shifts',
  RECEPTION_SHIFTS_MANAGE: 'reception_shifts.manage',
  DOCTOR_SHIFTS: 'doctor_shifts',
  DOCTOR_SHIFTS_MANAGE: 'doctor_shifts.manage',
  MEDICAL_RECORDS: 'medical_records',
  IMAGING_STUDIES: 'imaging_studies',
  MAILINGS: 'mailings',
  EXPENSES: 'expenses',
  PAYROLL: 'payroll',
  PAYROLL_MANAGE: 'payroll.manage',
  ATTENDANCE: 'attendance',
  ATTENDANCE_MANAGE: 'attendance.manage',
  MASTERS: 'masters',
  SERVICES: 'services',
  SETTINGS_AI: 'settings.ai',
  SETTINGS_USERS: 'settings.users',
  FEEDBACK_INBOX: 'feedback.inbox',
  PHARMACY: 'pharmacy',
};

/** @type {Record<string, string>} path (без layout) → permission */
export const ROUTE_PERMISSIONS = {
  '/dashboard': PERMISSIONS.DASHBOARD,
  '/reception': [
    PERMISSIONS.UPCOMING_APPOINTMENTS,
    PERMISSIONS.SCHEDULE,
    PERMISSIONS.MEDICAL_RECORDS,
    PERMISSIONS.IMAGING_STUDIES,
    PERMISSIONS.CLIENTS,
  ],
  '/schedule': PERMISSIONS.SCHEDULE,
  '/upcoming': PERMISSIONS.UPCOMING_APPOINTMENTS,
  '/clients': [PERMISSIONS.CLIENTS, PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.IMAGING_STUDIES, PERMISSIONS.RECEPTION_LEADS],
  '/reception-leads': PERMISSIONS.RECEPTION_LEADS,
  '/inpatient': [PERMISSIONS.SCHEDULE, PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.CLIENTS],
  '/surgery': [PERMISSIONS.SCHEDULE, PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.CLIENTS],
  '/finance': [PERMISSIONS.EXPENSES, PERMISSIONS.PAYROLL, PERMISSIONS.DASHBOARD_FINANCE],
  '/reports': PERMISSIONS.DASHBOARD_FINANCE,
  '/payments': PERMISSIONS.DASHBOARD_FINANCE,
  '/masters': [PERMISSIONS.MASTERS, PERMISSIONS.SERVICES],
  '/attendance': PERMISSIONS.ATTENDANCE,
  '/feedback-inbox': PERMISSIONS.FEEDBACK_INBOX,
  '/templates': [PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.IMAGING_STUDIES],
  '/settings': [PERMISSIONS.SETTINGS_AI, PERMISSIONS.SETTINGS_USERS, PERMISSIONS.MAILINGS],
  '/pharmacy': PERMISSIONS.PHARMACY,
};

export function routePermission(path, route) {
  if (route?.permissions?.length) return route.permissions;
  if (!path) return null;
  const mapped = ROUTE_PERMISSIONS[path];
  if (mapped) return mapped;
  if (path.startsWith('/medical-records/')) {
    return [PERMISSIONS.MEDICAL_RECORDS, PERMISSIONS.IMAGING_STUDIES];
  }
  if (path.startsWith('/upcoming/')) return PERMISSIONS.UPCOMING_APPOINTMENTS;
  if (path.startsWith('/settings')) {
    return [PERMISSIONS.SETTINGS_AI, PERMISSIONS.SETTINGS_USERS, PERMISSIONS.MAILINGS];
  }
  return null;
}

export function isImagingOnlyUser(user) {
  if (!user) return false;
  if (user.isDirector || user.role === 'director') return false;
  const perms = normalizeUserPermissions(user);
  return (
    perms.includes(PERMISSIONS.IMAGING_STUDIES) && !perms.includes(PERMISSIONS.MEDICAL_RECORDS)
  );
}

export function isDirectorOrAdminUser(user) {
  if (!user) return false;
  if (user.isDirector || user.role === 'director') return true;
  if (user.isAdmin || user.role === 'admin') return true;
  return false;
}

export function canAccessRoute(user, route) {
  if (userUsesTaxView(user) && route?.path && isRouteHiddenInTaxView(route.path)) {
    return false;
  }
  if (userUsesMirrorLedger(user) && route?.path && isRouteHiddenInMirrorLedger(route.path)) {
    return false;
  }
  if (route?.directorOnly && !(user?.isDirector || user?.role === 'director')) {
    return false;
  }
  if (route?.adminOrDirector && !isDirectorOrAdminUser(user)) {
    return false;
  }
  if (route?.permissions?.length) {
    return route.permissions.some((p) => userHasPermission(user, p));
  }
  const perm = route?.permission ?? (typeof route?.path === 'string' ? routePermission(route.path, route) : null);
  if (Array.isArray(perm)) return perm.some((p) => userHasPermission(user, p));
  if (!perm) return true;
  return userHasPermission(user, perm);
}

export function normalizeUserPermissions(user) {
  if (!user) return [];
  const list = user.permissions ?? user.permissionKeys ?? [];
  return Array.isArray(list) ? list : [];
}

export function userHasPermission(user, permission) {
  if (!user) return false;
  if (user.isDirector || user.role === 'director') return true;
  const perms = normalizeUserPermissions(user);
  return perms.includes(permission);
}

/** Создание и редактирование карточек пациентов (регистратура, админ, УЗИ). */
export function canManageClientRecords(user) {
  if (!user) return false;
  if (user.isDirector || user.role === 'director') return true;
  if (user.role === 'admin' || user.isAdmin) return true;
  if (user.role === 'imaging' || user.role === 'surgeon') return true;
  const perms = normalizeUserPermissions(user);
  const isDoctor = user.role === 'doctor' || user.role === 'surgeon' || user.role === 'anesthesiologist' || user.isDoctor;
  if (isDoctor) {
    return perms.includes(PERMISSIONS.IMAGING_STUDIES);
  }
  return perms.includes(PERMISSIONS.CLIENTS);
}

export function filterRoutesByPermissions(routes, user) {
  return routes
    .map((route) => {
      if ((route.category || route.collapse) && Array.isArray(route.items)) {
        const items = filterRoutesByPermissions(route.items, user);
        if (items.length === 0) {
          return null;
        }

        return { ...route, items };
      }

      if (route.layout !== '/admin') {
        return route;
      }

      if (userUsesTaxView(user) && route.path && isRouteHiddenInTaxView(route.path)) {
        return null;
      }
      if (userUsesMirrorLedger(user) && route.path && isRouteHiddenInMirrorLedger(route.path)) {
        return null;
      }

      if (route.directorOnly && !(user?.isDirector || user?.role === 'director')) {
        return null;
      }

      if (route.adminOrDirector && !isDirectorOrAdminUser(user)) {
        return null;
      }

      if (route.hideInSidebar && !route.component) {
        return route;
      }

      if (route.permissions?.length) {
        return route.permissions.some((p) => userHasPermission(user, p)) ? route : null;
      }

      const perm = route.permission ?? routePermission(route.path, route);
      if (!perm) {
        return route;
      }
      if (Array.isArray(perm)) {
        return perm.some((p) => userHasPermission(user, p)) ? route : null;
      }

      return userHasPermission(user, perm) ? route : null;
    })
    .filter(Boolean);
}

export function firstAllowedAdminPath(user, routes = []) {
  if (user?.isAnesthesiologist || user?.role === 'anesthesiologist') {
    return '/admin/surgery';
  }

  for (const route of flattenAdminRoutes(routes)) {
    if (route.hideInSidebar) {
      continue;
    }
    if (canAccessRoute(user, route)) {
      const base = route.path.split('/:')[0];
      return `/admin${base}`;
    }
  }
  return '/auth/sign-in';
}
